import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';

/**
 * CourseEditorModal Component
 * Modal for creating or editing courses
 * 
 * @param {Object} props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Callback when modal is closed
 * @param {Function} props.onSave - Callback when form is saved (courseData)
 * @param {Object} props.course - Existing course to edit (null for new course)
 */
export function CourseEditorModal({ show, onHide, onSave, course }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    maxStudents: '',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens/closes or course changes
  useEffect(() => {
    if (show) {
      if (course) {
        setFormData({
          title: course.title || '',
          description: course.description || '',
          maxStudents: course.maxStudents != null ? String(course.maxStudents) : '',
        });
      } else {
        setFormData({
          title: '',
          description: '',
          maxStudents: '',
        });
      }
      setError(null);
    }
  }, [show, course]);

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Course title is required');
      return;
    }

    if (formData.maxStudents !== '' && (isNaN(Number(formData.maxStudents)) || Number(formData.maxStudents) < 1)) {
      setError('Max students must be a positive number, or leave blank for unlimited');
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...formData,
        maxStudents: formData.maxStudents !== '' ? Number(formData.maxStudents) : null,
      });
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!saving) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {course ? 'Edit Course' : 'Create New Course'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Course Title <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter course title"
              required
              disabled={saving}
            />
            <Form.Text className="text-muted">
              A clear, descriptive title for your course
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Course Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter course description"
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Describe what students will learn in this course
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Max Students Enrollment</Form.Label>
            <Form.Control
              type="number"
              name="maxStudents"
              value={formData.maxStudents}
              onChange={handleChange}
              placeholder="Leave blank for unlimited"
              min="1"
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Maximum number of students that can enroll. Leave blank for unlimited.
            </Form.Text>
          </Form.Group>

          {course && (
            <div className="text-muted small">
              <p className="mb-1">Course ID: <code>{course.id}</code></p>
              {course.sections && (
                <p className="mb-0">
                  {course.sections.length} module{course.sections.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : (course ? 'Update Course' : 'Create Course')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
